import React, { useState, useMemo } from 'react';
import { forNamespace } from 'app/common/lib/util/i18n';
import {
  TeamfulCollaborationAddPeopleSortQuery,
  useTeamfulCollaborationAddPeopleQuery,
  useTeamfulCollaborationAddPeopleSortQuery,
  useTeamfulCollaborationAddPeopleToOrgMutation,
  Member,
} from '@trello/graphql/generated';
import styles from './TeamfulCollaborationAddPeople.less';
import {
  Button,
  ButtonSizes,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { Spinner } from 'app/src/components/Spinner';
import { Checkbox } from '@trello/nachos';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import { Noun, Verb, trackUe } from '@trello/analytics';
import classNames from 'classnames';

const format = forNamespace('teamful collaboration prompt');

const noop = () => {};

type MemberType = TeamfulCollaborationAddPeopleSortQuery['members'][0];

const memberComparator = (memberA: MemberType, memberB: MemberType): number => {
  const fullNameA = memberA.nonPublic?.fullName || memberA.fullName || '';
  const fullNameB = memberB.nonPublic?.fullName || memberB.fullName || '';
  const a = fullNameA.toLocaleLowerCase();
  const b = fullNameB.toLocaleLowerCase();
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

const trackAddedPeople = (
  orgId: string,
  addedMembersCount: number,
  availableMembersCount: number,
): void => {
  trackUe({
    category: Noun.TEAM_PAGE,
    verb: Verb.SUBMITS,
    directObj: Noun.TEAMLESS_COLLABORATION_PROMPT,
    context: {
      orgId,
      addedMembersCount,
      availableMembersCount,
    },
  });
};

interface TeamfulCollaborationAddPeopleProps {
  onContinue: () => void;
  orgId: string;
}

export const TeamfulCollaborationAddPeople: React.FunctionComponent<TeamfulCollaborationAddPeopleProps> = ({
  onContinue,
  orgId,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [membersToAdd, setMembersToAdd] = useState(new Set());
  const [addMembersToOrg] = useTeamfulCollaborationAddPeopleToOrgMutation();

  const { data, loading } = useTeamfulCollaborationAddPeopleQuery({
    fetchPolicy: 'no-cache',
    variables: { orgId },
  });

  const organization = data && data.organization;
  const boards = organization && organization.boards;

  // Get ids of the people already in the organization
  const orgMemberIds: string[] = useMemo(
    () =>
      (organization &&
        organization.members &&
        organization.members.map((member: { id: string }) => member.id)) ||
      [],
    [organization],
  );

  // Get a list of ids of people not in the organization but on boards in the organization
  const eligibleMemberIds: string[] = useMemo(() => {
    const ids: string[] = [];
    if (boards && boards.length) {
      for (const board of boards) {
        if (board && board.memberships) {
          for (const membership of board.memberships) {
            if (
              membership &&
              membership.idMember &&
              !membership.unconfirmed &&
              !orgMemberIds.includes(membership.idMember) &&
              !ids.includes(membership.idMember)
            ) {
              ids.push(membership.idMember);
            }
          }
        }
      }
    }
    return ids;
  }, [boards, orgMemberIds]);

  // With the list of eligible member ids, get the member data necessary to sort them
  const eligibleMembersQuery = useTeamfulCollaborationAddPeopleSortQuery({
    variables: { memberIds: eligibleMemberIds },
  });

  // Sort the members— this array will be used to render member items in a list
  const sorting = eligibleMembersQuery.loading;
  const membersToSort = eligibleMembersQuery.data?.members || [];
  const sortedMembers = useMemo(
    () => membersToSort.slice(0).sort(memberComparator),
    [membersToSort],
  );

  const onMemberCheckboxAreaClicked = (
    memberId: string,
    isChecked: boolean,
  ) => {
    // membersToAdd state is a Set with add and delete methods— here we choose which based on isChecked
    membersToAdd[isChecked ? 'add' : 'delete'](memberId);
    setMembersToAdd(new Set(membersToAdd));
  };

  const onSubmit = async () => {
    setIsSubmitted(true);

    if (membersToAdd.size) {
      const { data: addMembersData } = await addMembersToOrg({
        variables: {
          orgId,
          users: Array.from(membersToAdd).map((id: string) => ({ id })),
        },
      });

      const error =
        addMembersData &&
        addMembersData.addMembersToOrg &&
        addMembersData.addMembersToOrg.error;
      if (error) {
        Alerts.showLiteralText(error, 'error', 'alert', 5000);
        setIsSubmitted(false);
        return;
      }
    }

    trackAddedPeople(orgId, membersToAdd.size, eligibleMemberIds.length);
    onContinue();
  };

  if (loading || sorting) {
    return (
      <div className={styles.addPeopleLoadingState}>
        <Spinner centered />
        <div>{format(['loading'])}</div>
      </div>
    );
  } else if (sortedMembers.length === 0) {
    trackAddedPeople(orgId, 0, 0);
    onContinue();
    return null;
  }

  return (
    <div className={styles.addPeople}>
      <div className={styles.addPeopleTitle}>
        {format(['add collaborators form', 'title'])}
      </div>
      <div className={styles.addPeopleDescription}>
        {format(['add collaborators form', 'description'])}
      </div>
      <div className={styles.addPeopleList}>
        {sortedMembers?.map((member) => {
          const isChecked = membersToAdd.has(member.id);
          return (
            <PeopleListEntry
              member={member}
              isChecked={isChecked}
              onClick={onMemberCheckboxAreaClicked}
            />
          );
        })}
      </div>
      <div className={styles.addPeopleSubmitMessaging}>
        {format(['add collaborators form', 'submit messaging 2'])}
      </div>
      <Button
        buttonType={isSubmitted ? ButtonTypes.DISABLED : ButtonTypes.PRIMARY}
        className={styles.addPeopleButton}
        onClick={isSubmitted ? noop : onSubmit}
        size={ButtonSizes.FULLWIDTH}
      >
        {isSubmitted ? <Spinner centered /> : format(['next'])}
      </Button>
    </div>
  );
};

interface PeopleListEntryProps {
  member: Pick<Member, 'id' | 'nonPublic' | 'fullName'>;
  onClick: (memberId: string, isClicked: boolean) => void;
  isChecked: boolean;
}

const PeopleListEntry: React.FunctionComponent<PeopleListEntryProps> = ({
  member,
  onClick,
  isChecked,
}) => {
  const [isHovered, setHoverState] = useState(false);
  const name = member.nonPublic?.fullName || member.fullName || '';

  return (
    <div
      key={member.id}
      className={classNames(styles.addPeopleListItem, {
        [styles.addPeopleListItemHover]: isHovered,
      })}
      onClick={() => onClick(member.id, !isChecked)}
      onMouseEnter={() => setHoverState(true)}
      onMouseLeave={() => setHoverState(false)}
      role="checkbox"
      aria-checked={isChecked}
    >
      <MemberAvatar
        className={styles.addPeopleListItemAvatar}
        idMember={member.id}
        size={20}
      />
      <div className={styles.addPeopleListItemName}>{name}</div>
      <Checkbox
        className={styles.addPeopleListItemCheckbox}
        isChecked={isChecked}
      />
    </div>
  );
};
